import * as React from 'react';
import { CheckCircle2, AlertTriangle, AlertCircle, Info } from 'lucide-react';
import { cn } from '../lib/cn';

/**
 * Alert — inline messaging block.
 *
 * Tone variants: info / success / warning / error.
 * Pairs background tint + saturated icon for accessible color+icon signal.
 */

type Tone = 'info' | 'success' | 'warning' | 'error';

const config = {
  info: {
    bg: 'bg-info-50 border-info-100 text-info-800',
    icon: Info,
    iconClass: 'text-info-500',
    role: 'status' as const,
  },
  success: {
    bg: 'bg-success-50 border-success-100 text-success-800',
    icon: CheckCircle2,
    iconClass: 'text-success-500',
    role: 'status' as const,
  },
  warning: {
    bg: 'bg-warning-50 border-warning-100 text-warning-800',
    icon: AlertTriangle,
    iconClass: 'text-warning-500',
    role: 'alert' as const,
  },
  error: {
    bg: 'bg-error-50 border-error-100 text-error-800',
    icon: AlertCircle,
    iconClass: 'text-error-500',
    role: 'alert' as const,
  },
};

export interface AlertProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  tone?: Tone;
  title?: React.ReactNode;
  icon?: React.ReactNode;
  actions?: React.ReactNode;
}

export function Alert({
  tone = 'info',
  title,
  icon,
  actions,
  className,
  children,
  ...props
}: AlertProps) {
  const conf = config[tone];
  const IconComp = conf.icon;
  return (
    <div
      role={conf.role}
      className={cn('flex gap-3 rounded-lg border p-4', conf.bg, className)}
      {...props}
    >
      <span className={cn('mt-0.5 shrink-0', conf.iconClass)} aria-hidden="true">
        {icon ?? <IconComp className="h-5 w-5" />}
      </span>
      <div className="flex-1 min-w-0">
        {title ? <p className="font-medium text-sm">{title}</p> : null}
        {children ? (
          <div className={cn('text-sm', title ? 'mt-1 opacity-90' : '')}>{children}</div>
        ) : null}
        {actions ? <div className="mt-3 flex gap-2">{actions}</div> : null}
      </div>
    </div>
  );
}
